import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useGivenCansList } from "../hooks";
import { useProducts } from "../../products/hooks";
import { useUsers } from "../../users/hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";

interface ISearchForm {
  productId?: string;
  customerId?: string;
  givenById?: string;
}

export const GivenCansPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: productsData } = useProducts({ where: {}, limit: 100, offset: 0 });
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      productId: searchParams.get("productId") ?? "",
      customerId: searchParams.get("customerId") ?? "",
      givenById: searchParams.get("givenById") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/given-cans",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const productId = searchParams.get("productId");
  const customerId = searchParams.get("customerId");
  const givenById = searchParams.get("givenById");

  const where: any = {};
  if (productId) where.productId = +productId;
  if (customerId) where.customerId = +customerId;
  if (givenById) where.givenById = +givenById;

  const { data, loading } = useGivenCansList({ where });

  const productName = (id: number | null) =>
    productsData?.products?.results?.find((product) => product.id === id)?.name ?? `#${id}`;

  return (
    <>
      <Header
        title="Distributions gratuites"
        subtitle="Échantillons et promotions"
        buttons={[
          {
            actionText: "Nouvelle distribution",
            bgColor: "indigo",
            textColor: "white",
            link: `/given-can/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <Select label="Produit" className="w-full" {...form.register("productId")}>
                <option value="">Tous</option>
                {productsData?.products?.results?.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Distribué par" className="w-full" {...form.register("givenById")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.givenCansList?.results?.length ? (
            <EmptyList text="Aucune distribution gratuite" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Produit</TableCell>
                  <TableCell header align="right">
                    Quantité
                  </TableCell>
                  <TableCell header>Distribué par</TableCell>
                  <TableCell header>Commentaire</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.givenCansList?.results?.map((givenCans) => (
                  <TableRow
                    key={`given-cans-${givenCans.id}`}
                    onClick={() => navigate(`/given-can/${givenCans.id}/update`)}
                  >
                    <TableCell>{productName(givenCans.productId)}</TableCell>
                    <TableCell align="right">{givenCans.quantity}</TableCell>
                    <TableCell>
                      {givenCans.givenBy
                        ? `${givenCans.givenBy.firstname} ${givenCans.givenBy.lastname}`
                        : "-"}
                    </TableCell>
                    <TableCell>{givenCans.comment || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
